import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";

import { Button } from "components/ui/Button";
import { TabSwitcher } from "components/ui/TabSwitcher/TabSwitcher";
import { ConsultationBoxReadOnly } from "features/consultations/components/ConsultationBoxReadOnly";
import {
  EmailTab,
  FormTab,
  LabOrderTab,
  LetterTab,
  NoteTab,
  PrescriptionTab,
  ReferralTab,
  SickCertTab,
} from "features/consultations/components/tabs";
import { useActiveConsultation } from "features/consultations/hooks/useActiveConsultation";
import { useConsultationTabs } from "features/consultations/hooks/useConsultationTabs";
import { PatientDetailCard } from "features/patients/components/PatientDetailCard";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { usePracticeSettings } from "features/practice/hooks/usePracticeSettings";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { isValidPatientId } from "utils/isValidPatientId";
import { pathGenerators } from "utils/routes";

import { NavigationBar } from "../components/NavigationBar/NavigationBar";
import { ConsultationsTable } from "../PatientConsultationsPage/components/ConsultationsTable/ConsultationsTable";
import s from "./ConsultationEditPage.module.css";

const ConsultationEditPage = () => {
  usePracticeSettings();

  const { consultationId } = useParams();
  const navigate = useNavigate();
  const patientId = useCurrentPatientId();

  const { data: staffInfo } = useStaffInfo();
  const { data: patient } = usePatientData();
  const { data: activeConsultation } = useActiveConsultation(consultationId);

  useEffect(() => {
    if (!consultationId) {
      navigate(pathGenerators.global.home());

      return;
    }
    // This route is standalone (not under PatientLayout), so guard the patient
    // id here too — a malformed /patients/undefined/consultation/edit/:id link
    // must not render patient-scoped content.
    if (!isValidPatientId(patientId)) {
      navigate(pathGenerators.global.patient());
    }
  }, [consultationId, patientId, navigate]);

  const staffName = staffInfo?.staffMember
    ? `${staffInfo.staffMember.firstName} ${staffInfo.staffMember.lastName}`
    : "";

  const consultationStaffName = activeConsultation?.consultationStaff
    ? `${activeConsultation.consultationStaff.firstName ?? ""} ${
        activeConsultation.consultationStaff.lastName ?? ""
      }`.trim()
    : staffName;

  const consultationMoment = (() => {
    if (!activeConsultation) return null;
    if (activeConsultation.startTime)
      return moment(activeConsultation.startTime);
    if (activeConsultation.created) {
      const parsed = moment(activeConsultation.created, [
        "DD-MM-YYYY HH:mm",
        moment.ISO_8601,
      ]);
      return parsed.isValid() ? parsed : null;
    }
    return null;
  })();
  const isReadOnly =
    !!activeConsultation &&
    !!consultationMoment &&
    moment().diff(consultationMoment, "hours") >= 24;

  const { tab, setTab, tabOptions } = useConsultationTabs({
    isReadOnly: true,
    hasEmail: true,
  });

  return (
    <div>
      <NavigationBar />
      <div style={{ margin: "0 auto" }}>
        <div className={s.wrapper}>
          <div className={s.firstRowWrapper}>
            <div className={s.firstColumnWrapper}>
              <PatientDetailCard maxWidth={700} />
            </div>
            <div className={s.secondColumnWrapper}>
              <div className={s.pageTitleBox}>
                <h1
                  className={s.pageTitle}
                  data-testid="consultation-edit-title"
                >
                  Consultation
                </h1>
                <Button
                  variant="text"
                  onClick={() =>
                    navigate(pathGenerators.patient.consult(patientId))
                  }
                >
                  Start new consultation
                </Button>
              </div>
              <div className={s.secondColumnSubWrapper}>
                <ConsultationBoxReadOnly
                  patient={patient}
                  staffName={staffName}
                  consultation={activeConsultation}
                  onFinish={() => setTab("add-note")}
                  isReadOnly={isReadOnly}
                />
              </div>
            </div>
          </div>
          <div className={s.secondRowWrapper}>
            <div className={s.fullColumnWrapper}>
              <div className={s.consultationWrapper}>
                <div className={s.topWrapper}>
                  <h3 className={s.title}>Consultation</h3>
                  {/* {!!isReadOnly && tab === "add-note" && (
                    <p className={s.readOnlyBanner}>
                      This consultation is locked — more than 24 hours have
                      passed since it was created. Notes are view-only.
                    </p>
                  )} */}
                  {/* {tab !== "add-note" && (
                    <p className={s.readOnlyBanner}>
                      This section is locked. It was submitted as part of the
                      consultation and cannot be edited.
                    </p>
                  )} */}
                  <TabSwitcher
                    options={tabOptions}
                    value={tab}
                    onChange={setTab}
                    withOverflow
                    fullWidth
                    disabled={!activeConsultation}
                  />
                </div>
                {tab === "add-note" && (
                  <NoteTab
                    consultation={activeConsultation}
                    isDisabled={!activeConsultation}
                    isReadOnly={isReadOnly}
                    staffName={consultationStaffName}
                    currentStaffMemberId={staffInfo?.staffMember?.id}
                  />
                )}
                {tab === "add-prescription" && (
                  <PrescriptionTab consultationId={consultationId} isReadOnly />
                )}
                {tab === "add-referral" && (
                  <ReferralTab
                    consultationId={consultationId}
                    isReadOnly
                    onSuccess={() => setTab("add-note")}
                  />
                )}
                {tab === "add-lab-order" && <LabOrderTab isReadOnly />}
                {tab === "add-letter" && (
                  <LetterTab
                    consultationId={consultationId}
                    isReadOnly
                    autoSave={false}
                  />
                )}
                {tab === "add-sick-cert" && (
                  <SickCertTab consultationId={consultationId} isReadOnly />
                )}
                {tab === "add-form" && (
                  <FormTab consultation={activeConsultation} isReadOnly />
                )}
                {tab === "send-email" && (
                  <EmailTab consultationId={consultationId} isReadOnly />
                )}
              </div>
              <div className={s.consultationWrapper}>
                <div
                  className={s.topWrapper}
                  style={{ marginBottom: 0, borderBottom: "none" }}
                >
                  <h3 className={s.title}>
                    Other consultations for this patient
                  </h3>
                </div>
                <ConsultationsTable
                  patientIdVal={patient?.id}
                  itemsPerPage={5}
                  currentConsultationId={consultationId}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ConsultationEditPage };
